import { useCallback, useEffect, useId, useRef, useState } from "react"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { setKnownPains } from "@/services/sessions"
import Spinner from "@/components/Spinner/Spinner"
import PaperNote from "@/components/PaperNote/PaperNote"
import styles from "@/styles/components/KnownPainsModal.module.css"

/*
 * What the client already told the facilitator, written on notes before anyone
 * else is in the room.
 *
 * The notes are one array in Firestore, so every change writes the whole thing —
 * a per-index path would leave the tail shifted under the old indexes after a
 * removal in the middle. Typing is held as a local draft and committed on a
 * pause, the same bargain `useDebouncedField` makes for single fields: a note is
 * a sentence, and one write per keystroke is a write per character.
 *
 * Adding and removing commit at once, because they are decisions rather than
 * typing. Closing flushes, so nothing is lost to the scrim or to Escape.
 */

interface KnownPainsModalProps {
  sessionId: string
  notes: string[]
  onClose: () => void
}

const TITLE = "Add pain points"
const BLURB = "Write one per note. These start on the board, and anyone in the room can edit them."
const PLACEHOLDER = "The thing they keep complaining about"
const COMMIT_DELAY_MS = 500

/*
 * Every change here writes the same array, so one key covers the lot: adding a
 * note and removing one are the same write, and the local copy is what the
 * dialog renders from while it is out. The spinner says the array has not landed
 * yet — closing the dialog on a note that has not saved is the failure.
 */
const SAVE = "knownPains"

export default function KnownPainsModal({ sessionId, notes, onClose }: KnownPainsModalProps) {
  const id = useId()
  const panel = useRef<HTMLDivElement>(null)
  const { write, isPending } = useFirestoreWrite()
  const [draft, setDraft] = useState("")
  /*
   * The array as this dialog has it, or null while it matches Firestore. Remote
   * changes are ignored once there is a draft, so a snapshot landing mid-sentence
   * cannot move the cursor.
   */
  const [local, setLocal] = useState<string[] | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** The array typed but not yet written. */
  const uncommitted = useRef<string[] | null>(null)

  // Stable for as long as the dialog is open, so the unmount cleanup below
  // flushes through the same writer it was set up with.
  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const next = uncommitted.current
    uncommitted.current = null
    if (next !== null) write(() => setKnownPains(sessionId, next), SAVE)
  }, [sessionId, write])

  // Closing by any route — Done, the scrim, Escape, or the screen going away —
  // still saves what was typed.
  useEffect(() => flush, [flush])

  useEffect(() => {
    panel.current?.focus()
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  const live = local ?? notes

  /** A decision: written straight away, cancelling any pending keystrokes. */
  const saveNow = (next: string[]) => {
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = null
    uncommitted.current = null
    setLocal(next)
    write(() => setKnownPains(sessionId, next), SAVE)
  }

  const editNote = (index: number, text: string) => {
    const next = live.map((note, i) => (i === index ? text : note))
    setLocal(next)
    uncommitted.current = next

    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      flush()
    }, COMMIT_DELAY_MS)
  }

  const add = () => {
    const text = draft.trim()
    if (!text) return
    saveNow([...live, text])
    setDraft("")
  }

  const written = live.filter((note) => note.trim()).length
  const label = written === 0 ? "Nothing loaded yet" : `${written} on the board already`
  const saving = isPending(SAVE)

  return (
    <div className={styles.scrim} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={styles.dialog}
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-blurb`}
      >
        <div className={styles.head}>
          <h2 className={styles.title} id={`${id}-title`}>
            {TITLE}
          </h2>
          <p className={styles.blurb} id={`${id}-blurb`}>
            {BLURB}
          </p>
        </div>

        <div className={styles.body}>
          <div className={styles.draftNote}>
            <textarea
              className={styles.draftText}
              value={draft}
              placeholder={PLACEHOLDER}
              aria-label="New pain point"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter commits, so a note is one keystroke away. Shift keeps
                // the newline, because some of these run to two lines.
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault()
                  add()
                }
              }}
            />
          </div>

          <div className={styles.addRow}>
            <button
              type="button"
              className={styles.add}
              disabled={!draft.trim() || saving}
              aria-busy={saving || undefined}
              onClick={add}
            >
              {saving ? <Spinner size="sm" /> : null}
              Add note
            </button>
            <span className={styles.hint}>Enter adds it. Shift + Enter for a new line.</span>
          </div>

          {live.length > 0 ? (
            <div className={styles.loaded}>
              <span className={styles.loadedLabel}>{label}</span>
              <div className={styles.notes}>
                {live.map((note, index) => (
                  <PaperNote
                    // Position is the only identity these have — the array holds
                    // plain strings, and two notes can read the same.
                    key={index}
                    position={index}
                    // Compact: this is a list being reviewed before the session,
                    // not the round where someone is writing one.
                    size="sm"
                    className={styles.note}
                  >
                    <textarea
                      className={styles.noteText}
                      value={note}
                      placeholder={PLACEHOLDER}
                      aria-label={`Pain point ${index + 1}`}
                      onChange={(event) => editNote(index, event.target.value)}
                      onBlur={flush}
                    />
                    {/* Every note's remove writes the same array, so they all
                        wait together — a second removal read off the old array
                        would put the first one's note back. */}
                    <button
                      type="button"
                      className={styles.remove}
                      title="Remove"
                      aria-label={`Remove pain point ${index + 1}`}
                      disabled={saving}
                      onClick={() => saveNow(live.filter((_, i) => i !== index))}
                    >
                      ×
                    </button>
                  </PaperNote>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.foot}>
          {/*
           * Marked but not disabled. Closing flushes, and the write carries on
           * after the dialog has gone — so the spinner is the facilitator's
           * reason to wait a beat rather than a lock they cannot get out of if
           * the write never lands.
           */}
          <button
            type="button"
            className={styles.done}
            aria-busy={saving || undefined}
            onClick={onClose}
          >
            {saving ? <Spinner size="sm" /> : null}
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
